/**
 * feat-230 — `/trivia-management`: author trivia sets and their questions.
 *
 * Staff-only (StaffRoute), tenant-scoped, and deliberately shaped like
 * `QuizContentManagement`'s feat-107 master-detail editor rather than like
 * anything new: page shell → card + app bar → skeleton/error/empty/content body,
 * `window.confirm()` for deletes, inline banners (never toasts), and
 * "save = mutate then `await onReload()`" with no optimistic patching.
 * See `design/admin-page-notes.md`.
 *
 * TWO PANES, not three — trivia is set → question, one level shallower than
 * unit → lesson → question. Left: the set list. Right: the selected set's meta
 * (title + date window) over its reorderable question list. Question editing is
 * a modal so the list stays visible while reordering.
 *
 * Spanish is NOT typed here. Saving English fires the backend's `retranslate()`
 * and the Spanish rows land in the existing translation review queue.
 *
 * REVISED 2026-08-10: that queue does not hold the copy back. The backend serves
 * a translation at ANY status (`_translated` in `routes/mobile_trivia.py`), so
 * machine Spanish is live as soon as it is written and approving it is a later
 * upgrade. This file's author-facing copy said the opposite until today; if you
 * are about to restore "only reaches servers once it's approved", read that
 * function first.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
import { useTenant } from '../../contexts/TenantContext';
import {
  TriviaSet,
  TriviaQuestion,
  TriviaCorrectOption,
  createTriviaQuestion,
  createTriviaSet,
  deleteTriviaQuestion,
  deleteTriviaSet,
  isValidDateRange,
  listTriviaSets,
  todayIsoDate,
  updateTriviaQuestion,
  updateTriviaSet,
} from '../../services/triviaAdminService';

const OPTION_LETTERS: TriviaCorrectOption[] = ['a', 'b', 'c', 'd'];

// ============================================================
// Small form primitives (copied from PairingChallengeEditor — there is no
// shared component library and this page is not the place to start one)
// ============================================================

const Label: React.FC<{ children: React.ReactNode; htmlFor?: string }> = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-xs font-semibold text-inkMuted mb-1">
    {children}
  </label>
);

const ErrText: React.FC<{ msg?: string }> = ({ msg }) =>
  msg ? <p className="mt-1 text-xs text-danger-text">{msg}</p> : null;

const inputCls =
  'w-full rounded-lg border border-softBorder px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral/40';

const btnPrimary =
  'bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95 disabled:opacity-50';
const btnSecondary =
  'text-inkMuted text-sm font-semibold px-3 py-2 rounded-full hover:bg-gray-100';
const btnDestructive =
  'text-danger-text text-sm font-semibold px-3 py-2 rounded-full hover:bg-danger-surface';

// ============================================================
// Drafts + validation (exported: these ARE the two traps this page exists to
// avoid, so they are unit-testable without mounting a pane)
// ============================================================

export interface SetDraft {
  title: string;
  start_date: string;
  end_date: string;
}

export interface SetDraftErrors {
  title?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * `end_date == start_date` is VALID and is the common case (one set per day).
 * Only `end_date < start_date` is rejected, and it is rejected INLINE under the
 * date pair — not as a toast.
 */
export function validateSetDraft(draft: SetDraft): SetDraftErrors {
  const errors: SetDraftErrors = {};
  if (!draft.title.trim()) errors.title = 'Give the set a title.';
  if (!draft.start_date) errors.start_date = 'Pick a start date.';
  if (!draft.end_date) errors.end_date = 'Pick an end date.';
  if (draft.start_date && draft.end_date && !isValidDateRange(draft.start_date, draft.end_date)) {
    errors.end_date = 'The end date cannot be before the start date.';
  }
  return errors;
}

export interface QuestionDraft {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  /**
   * NULL is a real state here, unlike `QuestionDetailForm`'s always-'a'. C and D
   * are optional, so emptying the option that was marked correct has to clear
   * the mark — otherwise the set saves with `correct_option` naming a blank
   * option and the Flutter pop-up has no tile to highlight.
   */
  correct_option: TriviaCorrectOption | null;
  reveal_text: string;
  sort_order: number;
}

export type QuestionDraftErrors = Partial<
  Record<'question_text' | 'option_a' | 'option_b' | 'option_c' | 'option_d' | 'correct_option' | 'reveal_text', string>
>;

const optionField = (letter: TriviaCorrectOption) =>
  `option_${letter}` as 'option_a' | 'option_b' | 'option_c' | 'option_d';

/** Whether the correct-answer radio for `letter` may be selected at all. */
export function isOptionFilled(draft: QuestionDraft, letter: TriviaCorrectOption): boolean {
  return draft[optionField(letter)].trim() !== '';
}

/**
 * Apply an option-text edit, clearing `correct_option` when the edit empties the
 * option that was marked correct. This is the authoring half of the
 * `correct_option` letter trap; the Flutter half is filtering blank options out
 * of the tile list (Flutter brief §5.3). Both ends have to agree.
 */
export function patchOptionText(
  draft: QuestionDraft,
  letter: TriviaCorrectOption,
  value: string,
): QuestionDraft {
  const next: QuestionDraft = { ...draft, [optionField(letter)]: value };
  if (value.trim() === '' && draft.correct_option === letter) next.correct_option = null;
  return next;
}

export function validateQuestionDraft(draft: QuestionDraft): QuestionDraftErrors {
  const errors: QuestionDraftErrors = {};
  if (!draft.question_text.trim()) errors.question_text = 'Write the question.';
  if (!draft.option_a.trim()) errors.option_a = 'Option A is required.';
  if (!draft.option_b.trim()) errors.option_b = 'Option B is required — a question needs at least two options.';
  // No lettering gaps: `correct_option` is a LETTER, and a blank C between a
  // filled B and a filled D makes the letter→tile mapping ambiguous for anything
  // that filters blanks. Cheap to author around, silent to debug.
  if (!draft.option_c.trim() && draft.option_d.trim()) {
    errors.option_c = 'Fill option C before option D.';
  }
  if (!draft.correct_option) {
    errors.correct_option = 'Mark which option is correct.';
  } else if (!isOptionFilled(draft, draft.correct_option)) {
    // Unreachable through the UI (the radio is disabled and clears on empty);
    // kept so a future edit path cannot slip a blank correct answer past.
    errors.correct_option = 'The option marked correct is empty.';
  }
  if (!draft.reveal_text.trim()) errors.reveal_text = 'Write the fun fact shown after answering.';
  return errors;
}

export const hasErrors = (errors: object): boolean => Object.keys(errors).length > 0;

function questionToDraft(q: TriviaQuestion | null, defaultSort: number): QuestionDraft {
  if (!q) {
    return {
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_option: null,
      reveal_text: '',
      sort_order: defaultSort,
    };
  }
  return {
    question_text: q.question_text,
    option_a: q.option_a,
    option_b: q.option_b,
    option_c: q.option_c ?? '',
    option_d: q.option_d ?? '',
    correct_option: q.correct_option,
    reveal_text: q.reveal_text,
    sort_order: q.sort_order,
  };
}

/** Blank C/D are written as explicit NULL so the app renders a 2-/3-option card. */
function draftToBody(draft: QuestionDraft) {
  const orNull = (v: string) => (v.trim() === '' ? null : v.trim());
  return {
    question_text: draft.question_text.trim(),
    option_a: draft.option_a.trim(),
    option_b: draft.option_b.trim(),
    option_c: orNull(draft.option_c),
    option_d: orNull(draft.option_d),
    correct_option: draft.correct_option as TriviaCorrectOption,
    reveal_text: draft.reveal_text.trim(),
    sort_order: draft.sort_order,
  };
}

// ============================================================
// Presentation helpers
// ============================================================

/** `2026-08-17` → `Aug 17`. Split on `-` — never `new Date('YYYY-MM-DD')`,
 *  which parses as UTC midnight and renders the previous day west of Greenwich. */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = (iso || '').split('-').map((p) => parseInt(p, 10));
  if (!y || !m || !d) return iso || '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[m - 1]} ${d}`;
}

export function formatDateWindow(set: Pick<TriviaSet, 'start_date' | 'end_date'>): string {
  return set.start_date === set.end_date
    ? formatIsoDate(set.start_date)
    : `${formatIsoDate(set.start_date)} – ${formatIsoDate(set.end_date)}`;
}

/** Earliest start first, then title — the same precedence the backend serves in. */
export function sortSets(sets: TriviaSet[]): TriviaSet[] {
  return [...sets].sort(
    (a, b) => a.start_date.localeCompare(b.start_date) || a.title.localeCompare(b.title),
  );
}

const sortQuestions = (qs: TriviaQuestion[]): TriviaQuestion[] =>
  [...qs].sort((a, b) => a.sort_order - b.sort_order);

// ============================================================
// Question editor modal
// ============================================================

export interface TriviaQuestionEditorProps {
  /** null = create mode. */
  question: TriviaQuestion | null;
  setId: string;
  defaultSort: number;
  onSaved: () => Promise<void> | void;
  onCancel: () => void;
  onDeleted?: () => Promise<void> | void;
}

export const TriviaQuestionEditor: React.FC<TriviaQuestionEditorProps> = ({
  question,
  setId,
  defaultSort,
  onSaved,
  onCancel,
  onDeleted,
}) => {
  const [draft, setDraft] = useState<QuestionDraft>(() => questionToDraft(question, defaultSort));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCreate = question === null;
  const errors = validateQuestionDraft(draft);
  const visible = showErrors ? errors : {};

  const handleSave = async () => {
    setShowErrors(true);
    if (hasErrors(errors)) return;
    setSaving(true);
    setError(null);
    try {
      const body = draftToBody(draft);
      if (isCreate) await createTriviaQuestion(setId, body);
      else await updateTriviaQuestion(question!.id, body);
      await onSaved();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!question || !onDeleted) return;
    if (!window.confirm('Delete this question?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTriviaQuestion(question.id);
      await onDeleted();
    } catch (e) {
      setError(String(e));
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label={isCreate ? 'New question' : 'Edit question'}
    >
      <div className="bg-white border border-softBorder rounded-3xl shadow-xl w-full max-w-xl my-8 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-softBorder">
          <h3 className="font-display text-lg font-semibold">
            {isCreate ? 'New question' : 'Edit question'}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-3 py-2 text-xs">
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="trivia-question-text">Question</Label>
            <textarea
              id="trivia-question-text"
              rows={2}
              value={draft.question_text}
              onChange={(e) => setDraft({ ...draft, question_text: e.target.value })}
              placeholder="What year did the first store open?"
              className={inputCls}
            />
            <ErrText msg={visible.question_text} />
          </div>

          <div>
            <Label>Options &amp; correct answer</Label>
            <p className="text-xs text-inkMuted mb-2">
              A and B are required. Leave C and D blank for a two- or three-option question — the
              correct-answer mark can only sit on an option that has text.
            </p>
            <div className="space-y-2">
              {OPTION_LETTERS.map((letter) => {
                const filled = isOptionFilled(draft, letter);
                const correct = draft.correct_option === letter;
                const optional = letter === 'c' || letter === 'd';
                return (
                  <div
                    key={letter}
                    className={`flex items-center gap-2 bg-white rounded-xl px-2.5 py-2 border ${
                      correct ? 'border-success/50' : 'border-softBorder'
                    }`}
                  >
                    <input
                      type="radio"
                      name="trivia-correct-option"
                      checked={correct}
                      disabled={!filled}
                      onChange={() => setDraft({ ...draft, correct_option: letter })}
                      aria-label={`Mark option ${letter.toUpperCase()} correct`}
                      title={filled ? undefined : 'Add text to this option before marking it correct'}
                      className="accent-success disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs font-bold text-inkMuted w-4 uppercase">{letter}</span>
                    <input
                      value={draft[optionField(letter)]}
                      onChange={(e) => setDraft(patchOptionText(draft, letter, e.target.value))}
                      placeholder={`Option ${letter.toUpperCase()}${optional ? ' (optional)' : ''}`}
                      aria-label={`Option ${letter.toUpperCase()}`}
                      className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none"
                    />
                    {correct && <span className="text-success text-xs font-bold">✓</span>}
                  </div>
                );
              })}
            </div>
            <ErrText msg={visible.option_a} />
            <ErrText msg={visible.option_b} />
            <ErrText msg={visible.option_c} />
            <ErrText msg={visible.correct_option} />
          </div>

          <div>
            <Label htmlFor="trivia-reveal-text">Reveal text</Label>
            <textarea
              id="trivia-reveal-text"
              rows={3}
              value={draft.reveal_text}
              onChange={(e) => setDraft({ ...draft, reveal_text: e.target.value })}
              placeholder="The fun fact shown after they answer."
              className={inputCls}
            />
            <ErrText msg={visible.reveal_text} />
          </div>

          <p className="text-xs text-inkMuted">
            Spanish is generated automatically and reaches servers right away. Review the
            wording in{' '}
            <Link to="/quiz-translations" className="text-coral font-semibold hover:underline">
              Translations
            </Link>{' '}
            — approving replaces the machine wording with yours.
          </p>
        </div>

        <div className="border-t border-softBorder px-6 py-3 flex items-center justify-between gap-2">
          {!isCreate && onDeleted ? (
            <button type="button" onClick={handleDelete} disabled={saving} className={btnDestructive}>
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCancel} disabled={saving} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Left pane — set list
// ============================================================

export interface TriviaSetListProps {
  sets: TriviaSet[];
  selectedSetId: string | null;
  creating: boolean;
  onSelect: (setId: string) => void;
  onCreate: () => void;
}

export const TriviaSetList: React.FC<TriviaSetListProps> = ({
  sets,
  selectedSetId,
  creating,
  onSelect,
  onCreate,
}) => (
  <div className="border-r border-softBorder p-3 flex flex-col gap-2 overflow-y-auto">
    <button type="button" onClick={onCreate} className={`${btnPrimary} w-full`}>
      + New set
    </button>
    {creating && (
      <div className="rounded-2xl px-3 py-2.5 border border-coral/30 bg-coral-light text-sm font-semibold text-ink">
        New set (unsaved)
      </div>
    )}
    {sets.map((set) => {
      const selected = !creating && set.id === selectedSetId;
      return (
        <button
          type="button"
          key={set.id}
          onClick={() => onSelect(set.id)}
          aria-current={selected ? 'true' : undefined}
          className={`text-left rounded-2xl px-3 py-2.5 border ${
            selected ? 'bg-coral-light border-coral/30' : 'bg-white border-softBorder hover:bg-cream/60'
          }`}
        >
          <div className="text-sm font-semibold text-ink truncate">{set.title || '(untitled)'}</div>
          <div className="text-xs text-inkMuted mt-0.5">
            {formatDateWindow(set)} · {set.questions.length}{' '}
            {set.questions.length === 1 ? 'question' : 'questions'}
          </div>
        </button>
      );
    })}
  </div>
);

// ============================================================
// Right pane — set meta + question list
// ============================================================

export interface TriviaSetDetailProps {
  /** null = the unsaved "new set" draft. */
  set: TriviaSet | null;
  tenantId: string;
  onReload: () => Promise<void>;
  onSetCreated: (setId: string) => void;
  onSetDeleted: () => void;
  onCancelCreate: () => void;
}

export const TriviaSetDetail: React.FC<TriviaSetDetailProps> = ({
  set,
  tenantId,
  onReload,
  onSetCreated,
  onSetDeleted,
  onCancelCreate,
}) => {
  const isCreate = set === null;
  const [draft, setDraft] = useState<SetDraft>(() =>
    set
      ? { title: set.title, start_date: set.start_date, end_date: set.end_date }
      : { title: '', start_date: todayIsoDate(), end_date: todayIsoDate() },
  );
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<TriviaQuestion | null | undefined>(undefined);
  const [dragId, setDragId] = useState<string | null>(null);

  const errors = validateSetDraft(draft);
  const visible = showErrors ? errors : {};
  const questions = useMemo(() => (set ? sortQuestions(set.questions) : []), [set]);
  const nextSort = questions.reduce((m, q) => Math.max(m, q.sort_order), 0) + 1;

  const dirty =
    !isCreate &&
    (draft.title !== set!.title ||
      draft.start_date !== set!.start_date ||
      draft.end_date !== set!.end_date);

  const patch = (p: Partial<SetDraft>) => {
    setDraft((d) => {
      const next = { ...d, ...p };
      // Convenience only — moving the start past the end drags the end along, so
      // the common single-day case never lands in an invalid state.
      if (p.start_date && next.end_date && next.end_date < p.start_date) next.end_date = p.start_date;
      return next;
    });
  };

  const handleSaveSet = async () => {
    setShowErrors(true);
    if (hasErrors(errors)) return;
    setSaving(true);
    setError(null);
    const body = {
      title: draft.title.trim(),
      start_date: draft.start_date,
      end_date: draft.end_date,
    };
    try {
      if (isCreate) {
        const { id } = await createTriviaSet(tenantId, body);
        await onReload();
        onSetCreated(id);
      } else {
        await updateTriviaSet(set!.id, body);
        await onReload();
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSet = async () => {
    if (!set) return;
    if (!window.confirm(`Delete “${set.title}”? This removes its questions.`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTriviaSet(set.id);
      onSetDeleted();
      await onReload();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Drag-to-reorder: splice locally to get the intended order, then PUT every
   * row's new `sort_order` and reload. No optimistic patching — the reload is
   * what proves `sort_order` round-tripped.
   */
  const handleDrop = async (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = questions.map((q) => q.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setSaving(true);
    setError(null);
    try {
      await Promise.all(ids.map((id, i) => updateTriviaQuestion(id, { sort_order: i + 1 })));
      await onReload();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (question: TriviaQuestion) => {
    if (!window.confirm('Delete this question?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteTriviaQuestion(question.id);
      await onReload();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="overflow-y-auto p-6 space-y-5">
      {error && (
        <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-4 py-2 text-sm">
          {error}
        </div>
      )}

      {/* set meta */}
      <div className="bg-cream/70 border border-softBorder rounded-2xl p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-semibold">
            {isCreate ? 'New trivia set' : 'Set details'}
          </h3>
          {!isCreate && (
            <button type="button" onClick={handleDeleteSet} disabled={saving} className={btnDestructive}>
              Delete set
            </button>
          )}
        </div>

        <div>
          <Label htmlFor="trivia-set-title">Title</Label>
          <input
            id="trivia-set-title"
            value={draft.title}
            onChange={(e) => patch({ title: e.target.value })}
            placeholder="How it all started"
            className={inputCls}
          />
          <ErrText msg={visible.title} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="trivia-start-date">Start date</Label>
            <input
              id="trivia-start-date"
              type="date"
              value={draft.start_date}
              onChange={(e) => patch({ start_date: e.target.value })}
              className={inputCls}
            />
            <ErrText msg={visible.start_date} />
          </div>
          <div>
            <Label htmlFor="trivia-end-date">End date</Label>
            <input
              id="trivia-end-date"
              type="date"
              value={draft.end_date}
              onChange={(e) => patch({ end_date: e.target.value })}
              className={inputCls}
            />
            <ErrText msg={visible.end_date} />
          </div>
        </div>
        <p className="text-xs text-inkMuted">
          Both dates are inclusive, and they may be the same day. Whether today falls inside the
          window is decided by the backend in the server's location timezone.
        </p>

        <div className="flex items-center justify-end gap-2">
          {isCreate && (
            <button type="button" onClick={onCancelCreate} disabled={saving} className={btnSecondary}>
              Cancel
            </button>
          )}
          {!isCreate && !dirty && !saving ? (
            // Not a disabled CTA — a disabled coral pill reading "Saved" looks
            // like a primary action the admin is locked out of.
            <span className="text-xs text-inkMuted font-semibold px-3 py-2">All changes saved</span>
          ) : (
            <button type="button" onClick={handleSaveSet} disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : isCreate ? 'Create set' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      {/* questions */}
      {isCreate ? (
        <p className="text-sm text-inkMuted">Save the set to start adding questions.</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-inkMuted uppercase tracking-wide">
              {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </h4>
            <button type="button" onClick={() => setEditingQuestion(null)} className={btnPrimary}>
              + Add question
            </button>
          </div>

          {questions.length === 0 ? (
            <div className="bg-white border border-softBorder rounded-3xl p-8 text-center">
              <div className="text-5xl mb-3">🎲</div>
              <h4 className="font-display text-lg font-semibold">No questions yet</h4>
              <p className="text-sm text-inkMuted mt-1">
                Three to five short lore questions is about two minutes in the app.
              </p>
            </div>
          ) : (
            <ol className="space-y-1.5">
              {questions.map((q, i) => (
                <li
                  key={q.id}
                  draggable
                  onDragStart={() => setDragId(q.id)}
                  onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(q.id)}
                  className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 bg-white border border-softBorder hover:bg-cream/60"
                >
                  <span className="text-inkMuted/40 text-xs select-none cursor-grab" aria-hidden>
                    ⠿
                  </span>
                  <span className="text-xs font-bold text-inkMuted w-5 shrink-0">{i + 1}.</span>
                  <span className="flex-1 truncate text-sm font-semibold">
                    {q.question_text || '(no text)'}
                  </span>
                  <span className="text-[10px] font-bold rounded-md px-2 py-1 bg-cream-dark text-inkMuted shrink-0">
                    {[q.option_a, q.option_b, q.option_c, q.option_d].filter((o) => (o ?? '').trim() !== '').length}{' '}
                    options
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingQuestion(q)}
                    className="text-coral text-sm font-semibold hover:underline shrink-0"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteQuestion(q)}
                    className="text-danger-text text-sm font-semibold hover:underline shrink-0"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ol>
          )}
          <p className="text-xs text-inkMuted">
            Drag a row to reorder. Spanish is generated automatically and reaches servers right
            away. Review the wording in{' '}
            <Link to="/quiz-translations" className="text-coral font-semibold hover:underline">
              Translations
            </Link>{' '}
            — approving replaces the machine wording with yours.
          </p>
        </div>
      )}

      {editingQuestion !== undefined && set && (
        <TriviaQuestionEditor
          key={editingQuestion?.id ?? 'new-question'}
          question={editingQuestion}
          setId={set.id}
          defaultSort={nextSort}
          onSaved={async () => {
            setEditingQuestion(undefined);
            await onReload();
          }}
          onDeleted={async () => {
            setEditingQuestion(undefined);
            await onReload();
          }}
          onCancel={() => setEditingQuestion(undefined)}
        />
      )}
    </section>
  );
};

// ============================================================
// Page root
// ============================================================

const TwoPaneSkeleton: React.FC = () => (
  <div className="grid" style={{ gridTemplateColumns: '280px minmax(0,1fr)', minHeight: 480 }}>
    <div className="border-r border-softBorder p-3 space-y-2.5">
      <div className="h-9 bg-gray-100 rounded-full animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-5/6" />
    </div>
    <div className="p-6 space-y-3">
      <div className="h-5 bg-gray-100 rounded animate-pulse w-1/3" />
      <div className="h-28 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-11/12" />
    </div>
  </div>
);

export interface TriviaEditorProps {
  tenantId: string;
  sets: TriviaSet[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => Promise<void>;
}

export const TriviaEditor: React.FC<TriviaEditorProps> = ({
  tenantId,
  sets,
  loading,
  error,
  onReload,
}) => {
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const ordered = useMemo(() => sortSets(sets ?? []), [sets]);

  // Initialize / repair selection when content loads or the selected set vanishes.
  useEffect(() => {
    if (ordered.length === 0) return;
    if (selectedSetId && ordered.some((s) => s.id === selectedSetId)) return;
    setSelectedSetId(ordered[0].id);
  }, [ordered, selectedSetId]);

  const selectedSet = ordered.find((s) => s.id === selectedSetId) ?? null;

  let body: React.ReactNode;
  if (loading && !sets) {
    body = <TwoPaneSkeleton />;
  } else if (error && !sets) {
    body = (
      <div className="p-6">
        <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-2xl px-5 py-4 flex items-center gap-4">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h4 className="font-display font-semibold">Couldn't load trivia sets</h4>
            <p className="text-sm opacity-90">
              The request to <code className="bg-white/60 rounded px-1">/api/admin/trivia/sets</code>{' '}
              failed. Your unsaved edits are kept.
            </p>
          </div>
          <button
            onClick={onReload}
            className="bg-danger-text text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-110 shrink-0"
          >
            ↻ Retry
          </button>
        </div>
      </div>
    );
  } else if (ordered.length === 0 && !creating) {
    body = (
      <div className="p-10 flex justify-center">
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-8 text-center max-w-md">
          <div className="text-5xl mb-3">🎲</div>
          <h4 className="font-display text-lg font-semibold">No trivia sets yet</h4>
          <p className="text-sm text-inkMuted mt-1 mb-4">
            A set is a title, a date window, and a handful of lore questions. One set per day is the
            usual shape.
          </p>
          <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
            + Create your first set
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {error && sets && (
          <div className="mx-5 mt-4 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-4 py-2 text-sm flex items-center justify-between gap-3">
            <span>Refresh failed — showing the last loaded content. Unsaved edits are kept.</span>
            <button onClick={onReload} className="font-semibold hover:underline shrink-0">
              ↻ Retry
            </button>
          </div>
        )}
        <div className="grid" style={{ gridTemplateColumns: '280px minmax(0,1fr)', minHeight: 560 }}>
          <TriviaSetList
            sets={ordered}
            selectedSetId={selectedSetId}
            creating={creating}
            onSelect={(id) => {
              setCreating(false);
              setSelectedSetId(id);
            }}
            onCreate={() => setCreating(true)}
          />
          {creating || selectedSet ? (
            <TriviaSetDetail
              key={creating ? 'new-set' : selectedSet!.id}
              set={creating ? null : selectedSet}
              tenantId={tenantId}
              onReload={onReload}
              onSetCreated={(id) => {
                setCreating(false);
                setSelectedSetId(id);
              }}
              onSetDeleted={() => setSelectedSetId(null)}
              onCancelCreate={() => setCreating(false)}
            />
          ) : (
            <section className="flex items-center justify-center p-6 text-sm text-inkMuted">
              Select a set from the list.
            </section>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="bg-white border border-softBorder rounded-3xl shadow-sm overflow-hidden">
      <div className="bg-white border-b border-softBorder px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Trivia</h2>
          <p className="text-sm text-inkMuted mt-0.5">
            Short, unserious lore questions shown as a pop-up when a server opens the app. No XP, no
            streaks, no scorecard effect.
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <Link to="/quiz-translations" className="text-coral text-sm font-semibold hover:underline">
            Translations →
          </Link>
          <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
            + New set
          </button>
        </div>
      </div>
      {body}
    </div>
  );
};

const TriviaManagement: React.FC = () => {
  const { selectedTenant } = useTenant();
  const [sets, setSets] = useState<TriviaSet[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    setError(null);
    try {
      setSets(await listTriviaSets(selectedTenant));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedTenant) reload();
    else setSets(null);
  }, [selectedTenant, reload]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-[1320px] mx-auto px-6 py-8">
        {!selectedTenant ? (
          <div className="p-4 bg-warning-surface border border-gold/30 rounded-2xl text-sm text-ink">
            No tenant selected. Return to a scorecard page and pick a tenant first.
          </div>
        ) : (
          <TriviaEditor
            tenantId={selectedTenant}
            sets={sets}
            loading={loading}
            error={error}
            onReload={reload}
          />
        )}
      </div>
    </div>
  );
};

export default TriviaManagement;
